import React, { useState, useEffect, FC } from "react";
import Content from "../../layout/content/Content";
import {
  Badge,
  Button,
  Card,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalBody,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import Head from "../../layout/head/Head";
import { Icon, TooltipComponent } from "../../components/Component";
import { FormProvider, useForm } from "react-hook-form";
import { Table } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import SearchInput from "../form-components/SearchComponent";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../utils/skeleton-templates/table";
import Swal from "sweetalert2";
import SequenceMaster from "./SequenceMaster";
import sequenceApis from "../../api/master/sequence";
import { projectsApis } from "../../api/projects";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { useLocation, useParams } from "react-router";
import { useDispatch } from "react-redux";
import { updateMenuData } from "../../store/slices/misc/menuData";
import TopMenu from "../../route/topMenu";
import { ColumnType } from "antd/es/table";
import {
  AddAccess,
  ViewAccess,
  EditAccess,
  CancelAccess,
  ApproveAccess,
  RejectAccess,
} from "../../components/accessControl/AccessControl";

export interface SequenceListViewProps {
  id: number;
  moduleName: string;
  moduleId: number;
  prefix: string;
  sequenceNo: number;
  createdDate: string;
  endDate: string;
  level: string;
  siteId: number;
  siteName: string;
  status: string;
  stateName?: string;
  projectId: string;
  projectSiteId: string;
}

const SequenceList: FC = () => {
  const location = useLocation();
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const dispatch = useDispatch();
  const { data: accessibility } = useSelector(
    (state: any) => state.accessibility
  );
  const { data: SideNavCollapse } = useSelector(
    (state: any) => state.SideNavCollapseStatus
  );
  const [allowedActions, setAllowedActions] = useState<any>({
    add: 0,
    view: 0,
    edit: 0,
    approve: 0,
    reject: 0,
    cancel: 0,
  });

  const [onSearchText, setSearchText] = useState("");
  const [originalData, setOriginalData] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [detail, setDetail] = useState<any>({});
  const {
    projectName: stateProjectName,
    id: stateProjectId,
    extra: {
      siteName: stateProjectSiteName,
      siteId: stateProjectSiteId,
    } = {} as any,
  } = location.state || {};
  const projectId = urlProjectId || stateProjectId;
  // Derived site info: prefer location.state, otherwise fetch from project API
  // (so deep-links / page reloads still populate the Site field).
  const [projectSiteId, setProjectSiteId] = useState<any>(stateProjectSiteId);
  const [projectSiteName, setProjectSiteName] = useState<string | undefined>(
    stateProjectSiteName
  );
  const [projectName, setProjectName] = useState<string | undefined>(
    stateProjectName
  );

  const [popUpContent, setPopUpContent] = useState<JSX.Element | null>(null);
  const methods = useForm({
    mode: "onChange",
  });
  const [dataSource, setDataSource] = useState<any>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    defaultCurrent: 1,
    defaultPageSize: 15,
    showSizeChanger: true,
    pageSizeOptions: ["15", "20", "30", "40", "50"],
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [screenSize, setScreenSize] = useState(0);

  const columns: ColumnType<SequenceListViewProps>[] = [
    {
      title: "Module",
      dataIndex: "moduleName",
      key: "moduleName",
      width: 150,
      fixed: "left",
      render(text: any, record: SequenceListViewProps) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              id={`sequence-${record?.id}`}
              onClick={() => {
                loadDetail(record.id);
                setViewModal(true);
              }}
            >
              {text}
            </span>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={""}
              id={`sequence-${record?.id}`}
              direction="right"
              text={"Click To View " + text + " Details"}
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },
    {
      title: "Prefix",
      dataIndex: "prefix",
      key: "prefix",
      width: 200,
    },
    {
      title: "Sequence No",
      dataIndex: "sequenceNo",
      key: "sequenceNo",
      width: 120,
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
      width: 150,
      render: (text: string) =>
        text ? dayjs(text).format("DD-MMM-YYYY") : "N/A",
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
      width: 150,
      render: (text: string) =>
        text ? dayjs(text).format("DD-MMM-YYYY") : "N/A",
    },
    {
      title: "Site",
      dataIndex: "siteName",
      key: "siteName",
      width: 220,
      render: (text: string) => text || "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (status: string) => (
        <span
          className="tb-status"
          style={{
            color: status === "Active" ? "#28a745" : "#dc3545",
            fontWeight: "500",
          }}
        >
          {status === "Active" ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (text: any, record: SequenceListViewProps) => {
        return (
          <div style={{ textAlign: "center" }}>
            {record?.status === "Active" ? (
              <UncontrolledDropdown direction="start">
                <DropdownToggle
                  tag="button"
                  className="btn btn-sm btn-icon" 
                  style={{
                    background: 'transparent', 
                    border: 'none',
                    boxShadow: 'none',
                    padding: '4px 8px'
                  }}
                >
                  <Icon name="more-h"></Icon>
                </DropdownToggle>
                <DropdownMenu>
                  <EditAccess moduleName="Projects">
                    <DropdownItem
                      onClick={() => handleModelPopUp(record.id, "edit")}
                    >
                      <Icon name="edit" className="me-2"></Icon>
                      Edit
                    </DropdownItem>
                  </EditAccess>
                  <DropdownItem onClick={() => handleDelete(record)}>
                    <Icon name="trash" className="me-2"></Icon>
                    In Active
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            ) : (
              <span className="text-muted">-</span>
            )}
          </div>
        );
      },
    }
  ];

  const fetchData = async () => {
    if (!projectId) {
      console.log("Current location.state:", location.state);
      return;
    }
    setLoading(true);
    try {
      const { data, status, message } = await sequenceApis.fetchSequence(
        "Project",
        projectId
      );
      // siteId: projectSiteId;

      if (status) {
        setDataSource(data);
        setOriginalData(data);
        setPagination((prev) => ({
          ...prev,
          total: data.length,
        }));
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (onSearchText !== "") {
      const searchText = onSearchText.toLowerCase();
      const filteredData = originalData.filter((item: any) => {
        return Object.values(item).some((value: any) => {
          return value && value.toString().toLowerCase().includes(searchText);
        });
      });
      setDataSource(filteredData);
      setPagination((prev) => ({
        ...prev,
        current: 1,
        total: filteredData.length,
      }));
    } else {
      setDataSource(originalData);
      setPagination((prev) => ({
        ...prev,
        current: 1,
        total: originalData.length,
      }));
    }
  }, [onSearchText, originalData]);

  const handleDelete = (_data: any) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes,In Active it!",
      allowOutsideClick: false,
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-primary",
        cancelButton: "btn btn-secondary",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        await deleteById(_data);
      }
    });
  };
  const deleteById = async (data: any) => {
    try {
      const { status, message } = await sequenceApis.updateSequenceStatusById(
        data.id
      );
      if (status) {
        fetchData();
        Swal.fire("Success", message, "success");
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (error) {
      console.log(error);
    }
  };

  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const refreshListView = () => {
    try {
      setPagination({
        current: 1,
        pageSize: 15,
        defaultCurrent: 1,
        defaultPageSize: 15,
        showSizeChanger: true,
        pageSizeOptions: ["15", "20", "30", "40", "50"],
        total: 0,
      });
      fetchData();
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  const loadDetail = (id: any) => {
    let index = dataSource.findIndex((item: any) => item.id === id);
    setDetail(dataSource[index]);
  };

  // When location.state is missing (page reload, deep-link), fetch the
  // project to derive site name/id so the Create Sequence Site field is
  // not left blank.
  useEffect(() => {
    const needsProjectFetch =
      !!projectId &&
      (!projectSiteId || !projectSiteName || !projectName);
    if (!needsProjectFetch) return;
    (async () => {
      try {
        const res: any = await projectsApis.getProjectById(
          projectId as any,
          undefined
        );
        if (res?.status && res?.data) {
          if (!projectSiteId && res.data.siteId != null) {
            setProjectSiteId(res.data.siteId);
          }
          if (!projectSiteName && res.data.siteName) {
            setProjectSiteName(res.data.siteName);
          }
          if (!projectName && res.data.projectTitle) {
            setProjectName(res.data.projectTitle);
          }
        }
      } catch (err) {
        console.error("Failed to fetch project for sequence list:", err);
      }
    })();
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => { };
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, [projectId]);

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => {
          return {
            ...item,
            linkState: location.state,
          };
        })
      )
    );
  }, [location, dispatch]);

  const formatDate = (date?: string | Date | dayjs.Dayjs | null): string => {
    if (!date) return "—";
    if (dayjs.isDayjs(date)) {
      return date.format("DD-MMM-YYYY");
    }
    const jsDate = new Date(date);
    return jsDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleModelPopUp = (id?: any, type?: string) => {
    const content = (
      <SequenceMaster
        formAction={type}
        id={id}
        projectSiteId={projectSiteId}
        projectSiteName={projectSiteName}
        activeModules={dataSource
          ?.filter((item: any) => item.status == "Active")
          ?.map((item: any) => item.moduleId)}
        toggleModelPopUp={() => {
          setIsOpen(false);
          fetchData();
        }}
        projectId={projectId}
      />
    );
    setPopUpContent(content);
    setIsOpen(true);
  };

  return (
    <>
      <Head title="" />
      <Content>
        <Card
          className="card-bordered"
          style={{
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 140px)",
            overflow: "hidden",
          }}
        >
          {/* Fixed Header Section */}
          <div
            style={{
              padding: "16px 24px",
              borderBottom: "1px solid #f0f0f0",
              flexShrink: 0,
            }}
          >
            <h5 style={{ marginBottom: "16px", fontWeight: "600" }}>
              {projectName} / Sequence
            </h5>
            <FormProvider {...methods}>
              <form>
                <div
                  className="d-flex justify-content-between align-items-center"
                  style={{ minHeight: "40px" }}
                >
                  <div style={{ width: "300px" }}>
                    <SearchInput
                      name="search"
                      placeholder="Search..."
                      width="100%"
                      prefixIcon={<SearchOutlined />}
                      onDebouncedSearch={(value: any) => {
                        setSearchText(value);
                      }}
                      debounceDelay={300}
                    />
                  </div>
                  <ul className="nk-ibx-head-tools g-1 m-0 p-0 d-flex align-items-center">
                    <li className="pt-0 pb-0">
                      <AddAccess moduleName="Projects">
                        <Button
                          className="toggle"
                          color="primary"
                          size="sm"
                          onClick={() => {
                            handleModelPopUp(null, "");
                          }}
                        >
                          <Icon name={"plus"} />
                          <span>Add Sequence</span>
                        </Button>
                      </AddAccess>
                    </li>
                    <li>
                      <ViewAccess moduleName="Master">
                        <UncontrolledDropdown>
                          <DropdownToggle
                            tag="a"
                            className="dropdown-toggle btn btn-icon btn-trigger"
                            onClick={(ev) => {
                              refreshListView();
                            }}
                          >
                            <Icon name="redo"></Icon>
                          </DropdownToggle>
                        </UncontrolledDropdown>
                      </ViewAccess>
                    </li>
                  </ul>
                </div>
              </form>
            </FormProvider>
          </div>

          {/* Scrollable Table Section */}
          <div style={{ flex: 1, overflow: "auto", padding: "16px 24px" }}>
            <SkeletonTable
              loading={loading}
              columns={columns as SkeletonTableColumnsType[]}
            >
              <Table
                rowKey="id"
                columns={columns}
                dataSource={dataSource}
                pagination={pagination}
                onChange={tableOnChange}
                bordered
                size="middle"
                scroll={{ x: 1400 }}
              />
            </SkeletonTable>
          </div>
        </Card>
      </Content>
      <Modal
        isOpen={isOpen}
        toggle={() => setIsOpen(false)}
        className="modal-dialog-centered"
        size="xl"
        backdrop="static"
      >
        <a
          href="#cancel"
          onClick={(ev) => {
            ev.preventDefault();
            setIsOpen(false);
          }}
          className="close"
        >
          <Icon name="cross-sm"></Icon>
        </a>

        <ModalBody>{popUpContent}</ModalBody>
      </Modal>
      <Modal
        isOpen={viewModal}
        toggle={() => setViewModal(false)}
        className="modal-dialog-centered"
        size="lg"
        backdrop="static"
      >
        <ModalBody>
          <a
            href="#cancel"
            onClick={(ev) => {
              ev.preventDefault();
              setViewModal(false);
            }}
            className="close"
          >
            <Icon name="cross-sm"></Icon>
          </a>
          <div className="nk-modal-head">
            <h4 className="nk-modal-title title">
              {detail?.moduleName} Sequence Details
            </h4>
          </div>
          <div className="nk-tnx-details mt-sm-3">
            <Row className="gy-3">
              <Col lg={6}>
                <span className="sub-text">PREFIX</span>
                <span className="caption-text">{detail?.prefix}</span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">SEQUENCE NO</span>
                <span className="caption-text">{detail?.sequenceNo}</span>
              </Col>
              {detail?.stateName ? (
                <Col lg={6}>
                  <span className="sub-text">State Name</span>
                  <span className="caption-text">{detail?.stateName}</span>
                </Col>
              ) : (
                <></>
              )}
              <Col lg={6}>
                <span className="sub-text">Created Date</span>
                <span className="caption-text">
                  {" "}
                  {formatDate(detail?.createdDate)}
                </span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">End Date</span>
                <span className="caption-text">
                  {" "}
                  {formatDate(detail?.endDate)}
                </span>
              </Col>

              <Col lg={6}>
                <span className="sub-text">Status</span>
                <Badge
                  color={detail?.status === "Active" ? "success" : "danger"}
                  size="md"
                >
                  {detail?.status === "Active" ? "Active" : "Inactive"}
                </Badge>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Set Sequence For</span>
                <span className="caption-text">
                  {!detail?.level
                    ? "N/A"
                    : detail?.level === "Org"
                      ? "Organization"
                      : "Site"}
                </span>
              </Col>
              {detail?.level == "Site" && (
                <Col lg={6}>
                  <span className="sub-text">Site</span>
                  <span className="caption-text">{detail?.siteName}</span>
                </Col>
              )}
            </Row>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default SequenceList;
